/**
 * The game editor (`03-client-app.md` section 4.4).
 *
 * Editing used to open a form inside the row it belonged to, which put a description box, six
 * artwork slots and a delete panel inside a list item and made the list unusable while any of
 * it was open. A game has more fields than a row has space, so it gets a screen.
 *
 * Two things shape it. Sections, because the questions fall into groups that are rarely
 * answered in the same sitting — the words, the storefronts, the art, the packaging — and a
 * single column of all of them is a scroll rather than a form. And a draft, because the save
 * sends only what changed: the toolbar can then say what is unsaved and where, Save is dead
 * until there is something to save, and leaving with work in the box is caught rather than
 * silently thrown away.
 *
 * Artwork is the exception and saves on the spot. An upload is already a round trip, so holding
 * it behind Save would mean a picture on screen that is not yet on the game.
 */

import * as React from 'react';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_IGNORE_GLOBS,
  SUGGESTED_GENRES,
  releaseDateProblem,
  type GameFeatureId,
  type GameKind,
  type GameLanguage,
  type SystemRequirements,
} from '@goodbuild/shared';
import { Badge, Button, Callout, Card, GameKindTag, Heading, Text } from '../ui/index.js';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import {
  ANDROID_PACKAGE_RULE,
  KindPicker,
  SteamDescriptionField,
  StoreUrlFields,
  storeProblems,
  useAdminGames,
  type AdminGame,
} from '../components/admin/gameFields.js';
import {
  FeaturePicker,
  GenreField,
  LanguageEditor,
  RequirementsEditor,
} from '../components/admin/gameDetailFields.js';
import { GameCreditsPanel } from '../components/admin/teamFields.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { FocusableTextField } from '../components/focus/OnScreenKeyboard.js';
import { GameArtwork } from '../components/game/GameArtwork.js';
import { GameTrailers } from '../components/game/GameTrailers.js';
import { Breadcrumbs } from '../components/layout/Breadcrumbs.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatRelative } from '../lib/format.js';
import { errorMessage } from '../lib/errors.js';
import { capabilities } from '../platform/index.js';

export const Route = createFileRoute('/admin/games/$id/edit')({ component: AdminGameEdit });

/* ------------------------------------------------------------------ draft */

/**
 * The editable fields, all as text.
 *
 * Every value is a string — including the order, which is a number on the game — because that
 * is what the fields hold, and because comparing what is in the boxes against what was loaded
 * is how this screen knows what to send. A half-typed `-` in a number field is a state the
 * form has to be able to be in.
 */
interface Draft {
  title: string;
  kind: GameKind;
  shortDescription: string;
  descriptionBb: string;
  sortOrder: string;
  steamUrl: string;
  epicUrl: string;
  androidPackageName: string;
  ignoreGlobs: string;
  developerName: string;
  publisherName: string;
  websiteUrl: string;
  releaseDate: string;
  releaseNote: string;
  contentNotice: string;
  legalNotice: string;
  /* The four that are not text. They are held as the shapes they are stored as rather than as
     something typed into a box, because that is what the controls hand back — and comparing
     them against what was loaded is what tells the toolbar there is something to save. */
  genres: string[];
  features: GameFeatureId[];
  languages: GameLanguage[];
  systemRequirements: SystemRequirements;
}

type Field = keyof Draft;

/** The fields a `!==` cannot answer for: two equal lists are two different arrays. */
const STRUCTURED: readonly Field[] = ['genres', 'features', 'languages', 'systemRequirements'];

function draftOf(game: AdminGame): Draft {
  return {
    title: game.title,
    kind: game.kind,
    shortDescription: game.shortDescription,
    descriptionBb: game.descriptionBb,
    sortOrder: String(game.sortOrder),
    steamUrl: game.steamUrl ?? '',
    epicUrl: game.epicUrl ?? '',
    androidPackageName: game.androidPackageName ?? '',
    // What is stored and nothing else. The suggested list is a button under the field rather
    // than a pre-fill, so an empty box means the game ignores nothing and reads as such.
    ignoreGlobs: game.ignoreGlobs.join('\n'),
    developerName: game.developerName ?? '',
    publisherName: game.publisherName ?? '',
    websiteUrl: game.websiteUrl ?? '',
    releaseDate: game.releaseDate ?? '',
    releaseNote: game.releaseNote ?? '',
    contentNotice: game.contentNotice ?? '',
    legalNotice: game.legalNotice ?? '',
    // The column is a TEXT default of 'EUR' and the form only knows two currencies, so anything
    // else that ever got in there reads as EUR rather than breaking the picker.
    genres: game.genres,
    features: game.features,
    languages: game.languages,
    systemRequirements: game.systemRequirements,
  };
}

/** One glob per line, blanks dropped. */
function globLines(text: string): string[] {
  return text
    .split('\n')
    .map((glob) => glob.trim())
    .filter(Boolean);
}

/**
 * The draft as it will be stored.
 *
 * Run once on save and written back into the fields, so the boxes end up showing what the game
 * now holds rather than what was typed at it — a title saved with a trailing space comes back
 * without one instead of staying dirty forever.
 *
 * The description is the one field left alone: it is Steam BBCode kept exactly as pasted so it
 * can go back into Steamworks unchanged, and trimming it would be an edit nobody asked for.
 */
function normalize(draft: Draft): Draft {
  return {
    ...draft,
    title: draft.title.trim(),
    shortDescription: draft.shortDescription.trim(),
    sortOrder: String(Number(draft.sortOrder.trim() || '0')),
    steamUrl: draft.steamUrl.trim(),
    epicUrl: draft.epicUrl.trim(),
    androidPackageName: draft.androidPackageName.trim(),
    ignoreGlobs: globLines(draft.ignoreGlobs).join('\n'),
    developerName: draft.developerName.trim(),
    publisherName: draft.publisherName.trim(),
    websiteUrl: draft.websiteUrl.trim(),
    releaseDate: draft.releaseDate.trim(),
    releaseNote: draft.releaseNote.trim(),
    contentNotice: draft.contentNotice.trim(),
    legalNotice: draft.legalNotice.trim(),
    // The four structured fields are normalised by their own schemas on the way to the column —
    // sorted, de-duplicated, emptied of blanks — so there is nothing left to do to them here.
  };
}

/**
 * Every field the save looks at.
 *
 * A field missing from this list is a field that can be edited and never saved, silently — which
 * is what the price and the currency were until the provider picker was built on top of
 * them and the values it filled in went nowhere.
 */
const FIELDS: Field[] = [
  'title',
  'kind',
  'shortDescription',
  'descriptionBb',
  'sortOrder',
  'developerName',
  'publisherName',
  'websiteUrl',
  'releaseDate',
  'releaseNote',
  'genres',
  'features',
  'languages',
  'systemRequirements',
  'contentNotice',
  'legalNotice',
  'steamUrl',
  'epicUrl',
  'androidPackageName',
  'ignoreGlobs',
];

/**
 * Whether a field still holds what it was loaded with.
 *
 * A string is compared as a string. The four that are not — the genres, the features, the
 * languages, the requirements — are compared as their JSON, because every keystroke in the
 * requirements editor builds a new object and `!==` would call the form dirty from the moment
 * it was opened. Their shapes are small, flat and written by us, so stringifying them is a
 * reliable comparison and not the shortcut it usually is.
 */
function sameField(field: Field, base: Draft, next: Draft): boolean {
  if (!STRUCTURED.includes(field)) return base[field] === next[field];
  return JSON.stringify(base[field]) === JSON.stringify(next[field]);
}

function changedFields(base: Draft, next: Draft): Field[] {
  return FIELDS.filter((field) => !sameField(field, base, next));
}

/**
 * The patch body, holding only the fields that moved.
 *
 * `updateGameBodySchema` is a partial, so an absent key is "leave it alone". Sending the whole
 * form instead would mean two admins editing different parts of the same game overwrite each
 * other with values neither of them touched.
 */
function patchFor(base: Draft, next: Draft): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const field of changedFields(base, next)) {
    switch (field) {
      case 'sortOrder':
        patch.sortOrder = Number(next.sortOrder);
        break;
      case 'ignoreGlobs':
        patch.ignoreGlobs = globLines(next.ignoreGlobs);
        break;
      // Cleared means null and not an empty string: the game page tests these for absence, and
      // `storeUrlSchema` and `nullableText` normalise them the same way.
      case 'steamUrl':
      case 'epicUrl':
      case 'androidPackageName':
      case 'developerName':
      case 'publisherName':
      case 'websiteUrl':
      case 'releaseDate':
      case 'releaseNote':
      case 'contentNotice':
      case 'legalNotice':
        patch[field] = next[field].trim() || null;
        break;
      default:
        patch[field] = next[field];
    }
  }
  return patch;
}

/** What is wrong with the draft, in the words the fields show. Empty means it can be saved. */
function draftProblems(draft: Draft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('A game needs a title.');
  if (draft.sortOrder.trim() && !Number.isInteger(Number(draft.sortOrder.trim()))) {
    problems.push('The order is a whole number.');
  }
  const android = draft.androidPackageName.trim();
  if (android && !ANDROID_PACKAGE_RULE.test(android)) {
    problems.push('The Android package name looks like com.studio.game.');
  }
  // The same function the API validates the date with, so a wrong one is caught while it is
  // being typed rather than on save.
  const release = releaseDateProblem(draft.releaseDate);
  if (release) problems.push(release);
  if (draft.websiteUrl.trim() && !/^https?:\/\/\S+$/i.test(draft.websiteUrl.trim())) {
    problems.push('The website starts with https:// and has no spaces in it.');
  }
  problems.push(...storeProblems({ steam: draft.steamUrl, epic: draft.epicUrl }));
  return problems;
}

/* --------------------------------------------------------------- sections */

const SECTIONS = [
  { id: 'details', label: 'Details' },
  { id: 'facts', label: 'Facts' },
  { id: 'features', label: 'Features' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'stores', label: 'Store links' },
  { id: 'team', label: 'Team' },
  { id: 'artwork', label: 'Artwork' },
  { id: 'trailers', label: 'Trailers' },
  { id: 'files', label: 'Files' },
  { id: 'delete', label: 'Delete' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/** Which section a field is asked in, so the nav can mark the one holding unsaved work. */
const FIELD_SECTION: Record<Field, SectionId> = {
  title: 'details',
  kind: 'details',
  shortDescription: 'details',
  descriptionBb: 'details',
  sortOrder: 'details',
  developerName: 'facts',
  publisherName: 'facts',
  websiteUrl: 'facts',
  releaseDate: 'facts',
  releaseNote: 'facts',
  genres: 'facts',
  contentNotice: 'facts',
  legalNotice: 'facts',
  features: 'features',
  languages: 'features',
  systemRequirements: 'requirements',
  steamUrl: 'stores',
  epicUrl: 'stores',
  androidPackageName: 'stores',
  ignoreGlobs: 'files',
};

/* ----------------------------------------------------------------- screen */

function AdminGameEdit() {
  const { id } = Route.useParams();
  const { isAdmin } = useAccess();
  const games = useAdminGames();

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (games.isLoading) return <Loading />;

  const game = games.data?.find((row) => row.id === id);
  if (!game) return <EmptyState title={'That game is not here any more.'} />;

  // Keyed on the game, so opening a different one starts a clean draft rather than carrying
  // the last one's edits across. A refetch of the same game does not remount, which is what
  // keeps an artwork upload from wiping a half-written description.
  return <GameEditor key={game.id} game={game} />;
}

function GameEditor({ game }: { game: AdminGame }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [section, setSection] = React.useState<SectionId>('details');
  const [draft, setDraft] = React.useState<Draft>(() => draftOf(game));
  /** What the game held at the last successful save. The difference from `draft` is the patch. */
  const [baseline, setBaseline] = React.useState<Draft>(() => draftOf(game));
  const [confirmSlug, setConfirmSlug] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: keys.admin.games });

  const set = <K extends Field>(field: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const changed = changedFields(baseline, draft);
  const problems = draftProblems(draft);
  const dirty = changed.length > 0;

  const save = useMutation({
    mutationFn: async () => {
      const next = normalize(draft);
      const patch = patchFor(baseline, next);
      if (Object.keys(patch).length > 0) await api.patch(`/admin/games/${game.id}`, patch);
      return next;
    },
    onSuccess: (next) => {
      setError(null);
      setDraft(next);
      setBaseline(next);
      refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  // Publishing is not part of the draft: it is one switch with one consequence, and holding it
  // behind Save would mean a game goes live as a side effect of fixing a typo.
  const togglePublished = useMutation({
    mutationFn: () => api.patch(`/admin/games/${game.id}`, { published: !game.published }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  /**
   * Whether leaving should be caught, in a ref so it can be answered from outside a render.
   *
   * Deleting a game navigates away on purpose while the draft may still be dirty, and the
   * blocker has to know that before the navigation rather than a render later.
   */
  const blocking = React.useRef(false);
  React.useEffect(() => {
    blocking.current = dirty;
  }, [dirty]);

  const remove = useMutation({
    mutationFn: () => api.delete(`/admin/games/${game.id}`, { confirmSlug: confirmSlug.trim() }),
    onSuccess: () => {
      blocking.current = false;
      refresh();
      void navigate({ to: '/admin/games' });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const canSave = dirty && problems.length === 0 && !save.isPending;

  // Read by the shortcut below, which is registered once and would otherwise be holding the
  // first render's idea of whether there is anything to save.
  const saveNow = React.useRef<() => void>(() => {});
  saveNow.current = () => {
    if (canSave) save.mutate();
  };

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return;
      // The browser's Save Page is never what is wanted on a form with a Save button.
      event.preventDefault();
      saveNow.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  /**
   * Leaving with unsaved work asks first.
   *
   * `withResolver` keeps the question inside the app: a native `confirm()` is styled by the
   * webview, cannot be reached from a controller, and the shells this runs in do not agree on
   * whether it appears at all.
   */
  const blocker = useBlocker({
    shouldBlockFn: () => blocking.current,
    enableBeforeUnload: () => blocking.current,
    withResolver: true,
  });

  const discard = () => {
    setDraft(baseline);
    setError(null);
  };

  const dirtySections = new Set(changed.map((field) => FIELD_SECTION[field]));
  const unsaved =
    changed.length === 1 ? '1 unsaved change' : `${String(changed.length)} unsaved changes`;

  return (
    <FocusZone focusKey="admin-game-editor" direction="column" className="gb-stack">
      <Breadcrumbs
        trail={[
          { label: 'Admin', link: { to: '/admin' } },
          { label: 'Games', link: { to: '/admin/games' } },
          { label: game.title },
        ]}
      />

      <GameHead
        game={game}
        onTogglePublished={() => togglePublished.mutate()}
        onBuilds={() => void navigate({ to: '/admin/games/$id/builds', params: { id: game.id } })}
        busy={togglePublished.isPending}
      />

      {blocker.status === 'blocked' ? (
        <Callout.Root color="amber">
          <Callout.Description>{`There ${changed.length === 1 ? 'is' : 'are'} ${unsaved} on this game.`}</Callout.Description>
          <div className="gb-row gb-row--tight">
            <Focusable onSelect={() => blocker.reset()} selectLabel={'Stay'} autoFocus>
              <Button size="2">{'Stay here'}</Button>
            </Focusable>
            <Focusable onSelect={() => blocker.proceed()} selectLabel={'Leave'}>
              <Button size="2" variant="ghost" color="danger">
                {'Leave without saving'}
              </Button>
            </Focusable>
          </div>
        </Callout.Root>
      ) : null}

      {/* Sections on the left, the state of the draft on the right. Sticky, because the
          description box is taller than the screen and a Save that scrolls away is a Save that
          gets forgotten. */}
      <div className="gb-editor__bar">
        <FocusZone focusKey="admin-game-sections" direction="row" className="gb-row gb-row--tight">
          <div className="gb-segmented" role="group" aria-label="Game sections">
            {SECTIONS.map((entry) => (
              <Focusable
                key={entry.id}
                onSelect={() => setSection(entry.id)}
                selectLabel={entry.label}
                className="gb-focusable--flat"
              >
                <button
                  type="button"
                  className="gb-segmented__item"
                  aria-pressed={section === entry.id}
                >
                  {entry.label}
                  {/* A section holding unsaved work says so even while another one is open. */}
                  {dirtySections.has(entry.id) ? (
                    <span className="gb-editor__dot" aria-label="unsaved changes" />
                  ) : null}
                </button>
              </Focusable>
            ))}
          </div>
        </FocusZone>

        <span className="gb-seat__spacer" />

        <div className="gb-row gb-row--tight">
          <Text size="1" color={dirty ? 'amber' : 'gray'}>
            {save.isPending ? 'Saving' : dirty ? unsaved : 'Saved'}
          </Text>
          <Focusable onSelect={discard} selectLabel={'Discard'} disabled={!dirty}>
            <Button size="1" variant="ghost" disabled={!dirty}>
              {'Discard'}
            </Button>
          </Focusable>
          <Focusable onSelect={() => save.mutate()} selectLabel={'Save'} disabled={!canSave}>
            <Button size="2" disabled={!canSave}>
              {save.isPending ? 'Saving' : 'Save'}
            </Button>
          </Focusable>
        </div>
      </div>

      {/* What is blocking the save, not a running commentary: every one of these is also shown
          under the field it belongs to. */}
      {problems.length > 0 ? (
        <Callout.Root color="amber">
          <Callout.Description>{problems.join(' ')}</Callout.Description>
        </Callout.Root>
      ) : null}

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
        </Callout.Root>
      ) : null}

      {section === 'details' ? (
        <Card size="3" className="gb-form">
          <FocusableTextField
            value={draft.title}
            onChange={(next) => set('title', next)}
            label={'Title'}
            maxLength={120}
          />
          <KindPicker value={draft.kind} onChange={(next) => set('kind', next)} />
          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.shortDescription}
              onChange={(next) => set('shortDescription', next)}
              label={'Short description'}
              placeholder={'The one line on the tile.'}
              maxLength={300}
            />
            <Text size="1" color="gray">
              {`The line under the title on the shelf. ${String(draft.shortDescription.length)} of 300.`}
            </Text>
          </div>
          <SteamDescriptionField
            value={draft.descriptionBb}
            onChange={(next) => set('descriptionBb', next)}
            steamUrl={draft.steamUrl.trim() || null}
          />
          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.sortOrder}
              onChange={(next) => set('sortOrder', next)}
              label={'Order on the shelf'}
              type="number"
            />
            <Text size="1" color="gray">
              {'Lower comes first. Games sharing a number fall back to their title.'}
            </Text>
          </div>
        </Card>
      ) : null}

      {section === 'facts' ? (
        <Card size="3" className="gb-form">
          <div className="gb-two">
            <FocusableTextField
              value={draft.developerName}
              onChange={(next) => set('developerName', next)}
              label={'Developer'}
              placeholder={'The studio that made it.'}
              maxLength={120}
            />
            <FocusableTextField
              value={draft.publisherName}
              onChange={(next) => set('publisherName', next)}
              label={'Publisher'}
              placeholder={'Usually the same name.'}
              maxLength={120}
            />
          </div>

          <div className="gb-two">
            <FocusableTextField
              value={draft.releaseDate}
              onChange={(next) => set('releaseDate', next)}
              label={'Release date'}
              placeholder={'2027-03-14'}
              maxLength={10}
              hint={'A year, a year and a month, or a full date. Leave it empty if it has none.'}
            />
            <FocusableTextField
              value={draft.releaseNote}
              onChange={(next) => set('releaseNote', next)}
              label={'Instead of a date'}
              placeholder={'When it is ready'}
              maxLength={120}
              hint={'Shown on its own when there is no date, and beside one when there is.'}
            />
          </div>

          <FocusableTextField
            value={draft.websiteUrl}
            onChange={(next) => set('websiteUrl', next)}
            label={'Website'}
            placeholder={'https://yourgame.example'}
            maxLength={500}
            type="url"
            hint={'The game\u2019s own site. Storefronts go under Store links.'}
          />

          <GenreField
            value={draft.genres}
            onChange={(next) => set('genres', next)}
            suggestions={SUGGESTED_GENRES}
          />

          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.contentNotice}
              onChange={(next) => set('contentNotice', next)}
              label={'What is in it'}
              placeholder={'Frequent violence. Flashing lights in the third chapter.'}
              maxLength={2000}
              multiline
              rows={3}
            />
            <Text size="1" color="gray">
              {
                'Anything somebody would want to know before installing it. Drawn on the game page above the description.'
              }
            </Text>
          </div>

          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.legalNotice}
              onChange={(next) => set('legalNotice', next)}
              label={'Legal notice'}
              placeholder={'\u00a9 2027 Your Studio. All rights reserved.'}
              maxLength={2000}
              multiline
              rows={3}
            />
            <Text size="1" color="gray">
              {'The copyright and trademark line, drawn small at the foot of the game page.'}
            </Text>
          </div>
        </Card>
      ) : null}

      {section === 'features' ? (
        <Card size="3" className="gb-form">
          <FeaturePicker value={draft.features} onChange={(next) => set('features', next)} />
          <LanguageEditor value={draft.languages} onChange={(next) => set('languages', next)} />
        </Card>
      ) : null}

      {section === 'requirements' ? (
        <Card size="3" className="gb-form">
          <RequirementsEditor
            value={draft.systemRequirements}
            onChange={(next) => set('systemRequirements', next)}
          />
        </Card>
      ) : null}

      {section === 'stores' ? (
        <Card size="3" className="gb-form">
          <StoreUrlFields
            values={{ steam: draft.steamUrl, epic: draft.epicUrl }}
            onChange={(store, next) => set(store === 'steam' ? 'steamUrl' : 'epicUrl', next)}
          />

          {/* Under the addresses, because whoever is filling one in is holding the store page
              the other lives on. They do different jobs: an address is a link, and these are
              what an ownership claim asks about. */}
          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.androidPackageName}
              onChange={(next) => set('androidPackageName', next)}
              label={'Android package name'}
              placeholder={'com.studio.game'}
              maxLength={120}
            />
            <Text size="1" color="gray">
              {
                'How the app finds an installed APK. It is the package in the manifest, not the store address.'
              }
            </Text>
          </div>
        </Card>
      ) : null}

      {/* Who worked on this game, in what roles, and what they did. Its own draft and its own
          Save, for the reason `GameCreditsPanel` gives: this is an ordered list of objects and
          the toolbar above measures a flat record of fields. */}
      {section === 'team' ? <GameCreditsPanel gameId={game.id} /> : null}

      {/* Saves on upload rather than on Save, so this panel has no draft of its own. */}
      {section === 'artwork' ? (
        <GameArtwork gameId={game.id} art={game} onChanged={refresh} />
      ) : null}

      {/* Its own section rather than the foot of the artwork panel. An upload here is a hundred
          megabytes and a progress bar, and burying that under six file pickers is burying the
          one thing on this screen that takes long enough to need watching. */}
      {section === 'trailers' ? (
        <Card size="2" className="gb-stack gb-stack--tight">
          <Heading size="3">{'Trailers'}</Heading>
          <GameTrailers gameId={game.id} trailers={game.trailers} onChanged={refresh} />
        </Card>
      ) : null}

      {section === 'files' ? (
        <Card size="3" className="gb-form">
          <div className="gb-stack gb-stack--tight">
            <FocusableTextField
              value={draft.ignoreGlobs}
              onChange={(next) => set('ignoreGlobs', next)}
              label={'Files to ignore'}
              placeholder={DEFAULT_IGNORE_GLOBS.join('\n')}
              maxLength={2000}
              multiline
            />
            <Text size="1" color="gray">
              {
                'One glob per line. These are dropped when a build is scanned, so they are never uploaded and never installed. An empty box ignores nothing.'
              }
            </Text>
            <div className="gb-row gb-row--tight">
              <Focusable
                onSelect={() => set('ignoreGlobs', DEFAULT_IGNORE_GLOBS.join('\n'))}
                selectLabel={'Use the usual list'}
                className="gb-focusable--flat"
              >
                <Button size="1" variant="surface">
                  {'Use the usual list'}
                </Button>
              </Focusable>
              <Text size="1" color="gray" mono>
                {DEFAULT_IGNORE_GLOBS.join('  ')}
              </Text>
            </div>
          </div>
        </Card>
      ) : null}

      {section === 'delete' ? (
        <Card size="3" className="gb-form">
          <div className="gb-stack gb-stack--tight">
            <Heading size="4">{'Delete this game'}</Heading>
            <Text size="2" color="gray">
              {
                'The game, its builds and every file behind them go. Anyone who has it installed keeps what is on their disk but stops getting updates. This cannot be undone.'
              }
            </Text>
          </div>
          {/* The address has to be typed back, so this cannot happen by a stray click or a
              stray A press. */}
          <FocusableTextField
            value={confirmSlug}
            onChange={setConfirmSlug}
            label={`Type ${game.slug} to confirm`}
            maxLength={64}
          />
          <div className="gb-row">
            <Focusable
              onSelect={() => remove.mutate()}
              selectLabel={'Delete'}
              disabled={confirmSlug.trim() !== game.slug || remove.isPending}
            >
              <Button
                size="2"
                color="danger"
                disabled={confirmSlug.trim() !== game.slug || remove.isPending}
              >
                {remove.isPending ? 'Deleting' : 'Delete this game'}
              </Button>
            </Focusable>
          </div>
        </Card>
      ) : null}
    </FocusZone>
  );
}

/** The game being edited, named and pictured, with the actions that are not fields. */
function GameHead({
  game,
  onBuilds,
  onTogglePublished,
  busy,
}: {
  game: AdminGame;
  onBuilds: () => void;
  onTogglePublished: () => void;
  busy: boolean;
}) {
  return (
    <Card size="3" className="gb-editor__head">
      {/* The shelf tile, at the shape the shelf draws it. It is the one thing on this screen
          that says which game is open without being read. */}
      <div className="gb-editor__thumb" data-empty={game.capsuleImageUrl ? undefined : true}>
        {game.capsuleImageUrl ? <img src={game.capsuleImageUrl} alt="" /> : null}
      </div>

      <div className="gb-stack gb-stack--tight gb-editor__title">
        <div className="gb-row gb-row--tight">
          <Heading size="4">{game.title}</Heading>
          <GameKindTag kind={game.kind} />
          {game.published ? (
            <Badge color="green">{'Published'}</Badge>
          ) : (
            <Badge color="gray">{'Draft'}</Badge>
          )}
        </div>
        <Text size="1" color="gray" mono>
          {`/games/${game.slug}`}
        </Text>
        <Text size="1" color="gray">
          {`Edited ${formatRelative(game.updatedAt)}`}
        </Text>
      </div>

      <span className="gb-seat__spacer" />

      <div className="gb-row gb-row--tight">
        {capabilities.uploadBuilds ? (
          <Focusable onSelect={onBuilds} selectLabel={'Builds'}>
            <Button size="1" variant="surface">
              {'Builds'}
            </Button>
          </Focusable>
        ) : null}
        <Focusable
          onSelect={onTogglePublished}
          selectLabel={game.published ? 'Unpublish' : 'Publish'}
          disabled={busy}
        >
          <Button size="1" variant="surface" disabled={busy}>
            {game.published ? 'Unpublish' : 'Publish'}
          </Button>
        </Focusable>
      </div>
    </Card>
  );
}
